import React, { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import { parseBriefsDrillParams, BRIEFS_BANNER_DEFAULTS } from '../lib/reportingDrill';
import HowItWorksButton from '../components/HowItWorksButton';
import Sidebar from '../components/Sidebar';
// One table treatment across all four Briefs tabs — List view included, which
// had been left on its own paddings, dividers and zebra striping.
import { QUEUE_TH_CELL, QUEUE_TD, QUEUE_ROW } from '../components/brief/queueTableStyles';
import FilterDropdown from '../components/FilterDropdown';
import SearchInput from '../components/SearchInput';
import BriefsBoard from '../components/BriefsBoard';
import TagChip from '../components/TagChip';
import TagFilterBar from '../components/TagFilterBar';
import { fetchTagsFor } from '../lib/tags';
import { fmtDate } from '../lib/dates';
// Avatar import dropped 2026-06 — owner-group avatars used to live
// in the brief group header; the row-sync brief replaced that
// elaborate header with the shared compact treatment.
import { PinButton } from '../components/pinBurst';
import { OttoLoading, OttoPageBar, OttoConfused } from '../components/otto';
import WaitingOnYou from '../components/brief/WaitingOnYou';
import DebriefReviewQueue from '../components/brief/DebriefReviewQueue';
import { reviewQueue } from '../lib/debriefReview';
import { applyDebriefTransition } from '../lib/debriefTransition';
import { notifyDebriefTransition } from '../lib/debriefNotify';
import BriefsOverdueList from '../components/brief/BriefsOverdueList';
import { fetchWaitingQuestions, fetchWaitingExtensionRequests, fetchWaitingUpdateRequests } from '../lib/waitingOnYou';
import ArchiveIcon from '../components/ArchiveIcon';
import { useActionToast } from '../components/ActionToast';
import '../components/archiveAnimation.css';
import AddButton from '../components/AddButton';
import GroupBySelect from '../components/GroupBySelect';
import GroupedListTable from '../components/GroupedListTable';
import { isBriefAtRisk, engagementMix, engagementSummary } from '../lib/atRisk';
import { deriveFeedbackDue } from '../lib/briefFeedback';
import FeedbackDueList from '../components/brief/FeedbackDueList';
import { fetchDraftPresenceForBriefs } from '../lib/internalResponses';

// Compact engagement bar for the at-risk Responses column (§3.3). Segment
// widths are proportional to the mix; the summary text and the bar's
// aria-label both carry the meaning, so it never rests on colour alone (§8).
const ENGAGEMENT_SEG = [
  { key: 'submitted', color: '#009159', label: 'submitted' },
  { key: 'started', color: '#92D400', label: 'started' },
  { key: 'accepted', color: '#2E8FA1', label: 'accepted' },
  { key: 'notStarted', color: '#CBD1BE', label: 'not started' },
  { key: 'declined', color: '#C6503A', label: 'declined' },
];
function EngagementBar({ mix }) {
  if (!mix || mix.total === 0) return <p className="font-['Metropolis'] text-sm text-[#51534A]">—</p>;
  const segs = ENGAGEMENT_SEG.filter(s => mix[s.key] > 0);
  const desc = segs.map(s => `${mix[s.key]} ${s.label}`).join(', ');
  return (
    <div>
      <div className="flex h-2 w-[128px] rounded-full overflow-hidden bg-[#EEF1E6]" role="img" aria-label={`Responses: ${desc}`}>
        {segs.map(s => (
          <span key={s.key} style={{ width: `${(mix[s.key] / mix.total) * 100}%`, background: s.color }} />
        ))}
      </div>
      <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey mt-1 whitespace-nowrap">{engagementSummary(mix)}</p>
    </div>
  );
}

export default function Briefs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Post-event feedback rows for awarded briefs, keyed by brief_id — drives the
  // "Feedback due" queue count + list. One source with the dashboard alert
  // (deriveFeedbackDue). The value-threshold setting isn't built yet, so 0.
  const [feedbackByBrief, setFeedbackByBrief] = useState({});
  // Per-row archive animation state. `archivingIds` drives the slide+fade
  // class on the matching <tr>; once the animation finishes the row is
  // removed from the visible list by the standard archived_at filter at
  // line ~238 (the DB write flips that flag in parallel with the anim).
  const [archivingIds, setArchivingIds] = useState(() => new Set());
  // Toasts (Undo flow) — useActionToast supports up to 3 stacked toasts
  // with countdown bars + action buttons. Each archive gets its own
  // 6-second Undo window.
  const { notify, Toast: ArchiveToast } = useActionToast();
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [projectNumberFilter, setProjectNumberFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [tagsByBrief, setTagsByBrief] = useState(new Map());
  // Live (non-deleted) comment counts per brief, populated alongside
  // getBriefs. Drives the small "💬 N" chip on list and board rows.
  const [commentCountsByBrief, setCommentCountsByBrief] = useState(new Map());
  const [briefTagFilter, setBriefTagFilter] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  // When set, the list filter is locked to "sent, past deadline, no response,
  // not archived" — the same definition the Reporting tab's Needs Attention
  // tile counts. Cleared via the dismiss button on the banner.
  const [needsAttentionOnly, setNeedsAttentionOnly] = useState(false);
  // At-risk filter (internal at-risk brief §3.2) — reached from the merged
  // banner, rendered as a removable chip. Filters to briefs heading for too
  // few quotes while there's still time to chase.
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  // Map<brief_id, Set<supplier_id>> of suppliers with a draft in progress —
  // the "started, not submitted" signal for the engagement mix. Existence
  // only, never draft content (§7).
  const [draftsByBrief, setDraftsByBrief] = useState(new Map());
  // "Waiting on you" data — loaded ONCE here so the tab count, the Otto banner
  // and the two group tables all read the same numbers (waiting-on-you §4).
  const [waitingQuestions, setWaitingQuestions] = useState([]);
  const [waitingExtensions, setWaitingExtensions] = useState([]);
  const [waitingUpdates, setWaitingUpdates] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [q, e, u] = await Promise.all([
        fetchWaitingQuestions(), fetchWaitingExtensionRequests(), fetchWaitingUpdateRequests(),
      ]);
      if (cancelled) return;
      setWaitingQuestions(q);
      setWaitingExtensions(e);
      setWaitingUpdates(u);
    })();
    return () => { cancelled = true; };
  }, []);
  const questionsWaiting = waitingQuestions.length;
  const extensionsWaiting = waitingExtensions.length;
  const chasesWaiting = waitingUpdates.length;
  const waitingTotal = questionsWaiting + extensionsWaiting + chasesWaiting;
  // Deep-link to a tab (dashboard banner / Otto bar). 'questions' stays an alias
  // for the renamed 'waiting' tab so existing links resolve.
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'questions' || t === 'waiting') setActiveTab('waiting');
    else if (t === 'overdue') setActiveTab('overdue');
    else if (t === 'feedback') setActiveTab('feedback');
    // ?tab=review comes from the digest. Gated on role, or a non-admin
    // following the link would land on a tab that isn't in their tab row —
    // a panel with no way back to it.
    else if (t === 'review' && isAdmin(currentInternalUser)) setActiveTab('review');
  }, [searchParams, currentInternalUser]);
  // Briefs briefing bar — × dismisses it only until the counts change
  // (waiting-on-you tidy-up §1). The band reports ongoing STATE (a question
  // still unanswered, a brief still overdue), so a plain "hide for the day"
  // would bury a blocked supplier. We store a signature of the actionable
  // counts on dismiss and re-show the moment any of them moves.
  const [ottoBarDismissedSig, setOttoBarDismissedSig] = useState(() => {
    try { return sessionStorage.getItem('otto_briefs_bar_dismissed_sig'); } catch (e) { return null; }
  });
  // Reporting drill-down filters — silent (no toolbar UI), only set by
  // dashboard tile clicks and cleared by the contextual banner's Clear
  // button or by the toolbar's Clear all.
  //   responseFilter: 'all' | 'with' | 'without'
  //   awardStateFilter: 'all' | 'awarded' | 'closed_no_award'
  //   dateRangeFilter: null | { start, end, field, label }
  const [responseFilter, setResponseFilter] = useState('all');
  const [awardStateFilter, setAwardStateFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState(null);
  // Bespoke banner copy applied when a drill-down sets non-visible filters.
  // Shape: { tone: 'amber'|'teal'|'green'|'grey', descriptor: string }.
  // "Showing {n} brief(s) {descriptor}" — see render below.
  const [drillBanner, setDrillBanner] = useState(null);
  // Sticky flag set when the page is reached via a Reporting drill-down
  // (URL contains ?ref=reporting). Drives the Back-to-Reporting affordance.
  // Stays set even after the user clears filters — spec says the back
  // link remains available until the user explicitly leaves.
  const [fromReporting, setFromReporting] = useState(false);
  // Per-user pin set so the leftmost row icon can toggle without
  // round-tripping through BriefDetail. Loaded once when the current
  // user resolves; mutations write through user_pinned_briefs.
  const [userPinnedBriefIds, setUserPinnedBriefIds] = useState(new Set());
  // Per-row checkbox selection — mirrors the Suppliers list pattern so
  // both list pages share one selection model (list-page consistency
  // brief B2). The toolbar above the table appears when the set is
  // non-empty and offers a bulk action; for now bulk Apply-Tag mirrors
  // Suppliers' existing flow. Bulk archive could land alongside via
  // the same toolbar if a concrete need surfaces.
  const [selectedForBulk, setSelectedForBulk] = useState(() => new Set());
  const toggleBulk = (id) => setSelectedForBulk(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  useEffect(() => {
    if (!currentInternalUser?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_pinned_briefs')
        .select('brief_id')
        .eq('user_id', currentInternalUser.id);
      if (cancelled) return;
      setUserPinnedBriefIds(new Set((data || []).map(r => r.brief_id)));
    })();
    return () => { cancelled = true; };
  }, [currentInternalUser?.id]);

  // Optimistic pin toggle — flips local state immediately, writes
  // through, rolls back on failure. stopPropagation on the click so
  // the icon doesn't also navigate the row to the brief detail page.
  const toggleBriefPin = async (briefId, e) => {
    if (e) e.stopPropagation();
    if (!currentInternalUser?.id || !briefId) return;
    const wasPinned = userPinnedBriefIds.has(briefId);
    setUserPinnedBriefIds(prev => {
      const next = new Set(prev);
      if (wasPinned) next.delete(briefId); else next.add(briefId);
      return next;
    });
    const { error } = wasPinned
      ? await supabase
          .from('user_pinned_briefs')
          .delete()
          .eq('user_id', currentInternalUser.id)
          .eq('brief_id', briefId)
      : await supabase
          .from('user_pinned_briefs')
          .upsert({ user_id: currentInternalUser.id, brief_id: briefId }, { onConflict: 'user_id,brief_id' });
    if (error) {
      setUserPinnedBriefIds(prev => {
        const next = new Set(prev);
        if (wasPinned) next.add(briefId); else next.delete(briefId);
        return next;
      });
    }
  };
  // Supplier-scoped drill filters from the Reporting dashboard.
  //   awardedSupplierFilter — only briefs where awarded_supplier_id = X
  //   respondingSupplierFilter — only briefs where this supplier submitted
  //     a response. brief_responses isn't on the briefs payload, so a side
  //     fetch resolves the matching brief_ids when this filter is set.
  const [awardedSupplierFilter, setAwardedSupplierFilter] = useState(null);
  const [respondingSupplierFilter, setRespondingSupplierFilter] = useState(null);
  const [respondedBriefIds, setRespondedBriefIds] = useState(null);

  // Side fetch for the responded-supplier filter. Null when the filter is
  // off; Set of brief_ids otherwise. Kept null while in-flight so the
  // table doesn't flash an unfiltered state before the lookup resolves.
  useEffect(() => {
    if (!respondingSupplierFilter) { setRespondedBriefIds(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('brief_responses')
        .select('brief_id')
        .eq('supplier_id', respondingSupplierFilter)
        .eq('is_submitted', true);
      if (cancelled) return;
      setRespondedBriefIds(new Set((data || []).map(r => r.brief_id)));
    })();
    return () => { cancelled = true; };
  }, [respondingSupplierFilter]);
  // Column sort. Default is brief_name asc — the user's primary scan axis
  // now that Brief Name is the table's leading column. Click a sortable
  // header to swap field; click the same field again to flip direction.
  // Default sort = deadline asc (most urgent first) because the table now
  // defaults to grouping by project, and within-group order is naturally
  // urgency-led. Users can override by clicking another column header.
  const [sortField, setSortField] = useState('required_response_time');
  const [sortDirection, setSortDirection] = useState('asc');
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Group by — defaults to 'none' (flat table, sort-only ordering). When
  // changed to any other value, briefs are bucketed by that field with a
  // subtle header row above each group.
  // Default to 'pinned' so the page opens with the PINNED group at
  // top — matches the user's expected default that pinned items are
  // surfaced as the first thing on load (rather than 'none' which
  // hides the section header and just sorts pinned items first).
  const [groupBy, setGroupBy] = useState('pinned');
  // Tracks group keys whose child rows are collapsed (hidden). Reset
  // whenever groupBy changes — see the auto-collapse effect below — so
  // the user's manual toggles only persist within a single grouping.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroupCollapsed = (key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    getBriefs();
    // Refetch when the tab regains focus / becomes visible, so a brief edited
    // elsewhere (another tab, or the detail page) — e.g. a deadline extended —
    // shows its new value on return instead of the value loaded on first mount.
    // Silent: no loading flicker, it's a background refresh.
    const onVisible = () => { if (document.visibilityState === 'visible') getBriefs({ silent: true }); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the feedback rows for awarded briefs so the "Feedback due" queue knows
  // which already have feedback. Re-runs whenever the briefs list refreshes.
  useEffect(() => {
    const awardedIds = briefs.filter(b => b.status === 'closed' && b.awarded_supplier_id).map(b => b.id);
    if (!awardedIds.length) { setFeedbackByBrief({}); return; }
    let alive = true;
    (async () => {
      const { data } = await supabase.from('brief_feedback')
        .select('brief_id, supplier_id, status, completed_at, delivered, on_time, safety, use_again')
        .in('brief_id', awardedIds);
      if (!alive) return;
      const map = {};
      for (const row of (data || [])) map[row.brief_id] = row;
      setFeedbackByBrief(map);
    })();
    return () => { alive = false; };
  }, [briefs]);

  // The feedback-due work-list + count (shared helper; oldest event first).
  const feedbackDueList = useMemo(
    () => deriveFeedbackDue(briefs, feedbackByBrief, { thresholdGBP: 0 }),
    [briefs, feedbackByBrief]);
  const feedbackDueCount = feedbackDueList.length;

  // ── Debriefs awaiting review ──────────────────────────────────────────────
  // ADMINS ONLY, and that means no chip, no count and no panel for anyone
  // else. A count you can't act on is noise, and it leaks how many debriefs
  // are in flight to people with no part in approving them.
  //
  // This does NOT overlap "Feedback due" (Rob confirmed, Q3): that counts
  // awarded briefs whose event has finished with no feedback recorded, this
  // counts debriefs somebody has submitted for approval. Different objects,
  // different actors, different resolutions — genuinely a second queue.
  const [pendingDebriefs, setPendingDebriefs] = useState([]);
  useEffect(() => {
    if (!isAdmin(currentInternalUser)) { setPendingDebriefs([]); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('project_debriefs')
        .select('id, brief_id, project_ref, project_name, client, status, submitted_at, written_by, written_by_user:internal_users!written_by(full_name)')
        .eq('status', 'pending_review');
      if (!cancelled) setPendingDebriefs(data || []);
    })();
    return () => { cancelled = true; };
  }, [currentInternalUser]);
  const reviewQueueRows = useMemo(() => reviewQueue(pendingDebriefs), [pendingDebriefs]);
  const awaitingReviewCount = reviewQueueRows.length;

  const getBriefs = async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    const { data, error } = await supabase
      .from('briefs')
      .select('id, brief_name, fusion_project_name, fusion_project_number, client, brief_category, brief_type, status, created_at, closed_at, required_response_time, is_privileged, archived_at, owner_id, awarded_supplier_id, awarded_amount, event_live_date, event_live_end_date, awarded_supplier:suppliers!awarded_supplier_id(id, supplier_name), owner:internal_users!owner_id(id, full_name, job_title, avatar_id, avatar_url), brief_owners(user_id, internal_users!user_id(id, full_name, job_title, avatar_id, avatar_url)), brief_responses(count), brief_recipients(supplier_id, status)')
      .order('created_at', { ascending: false });
    if (error) {
      // Surface the error instead of swallowing it — a silent return
      // here would leave the page in an "empty list" state that's
      // indistinguishable from a genuinely empty workspace.
      console.error('[Briefs] getBriefs failed:', error);
      if (!silent) setLoading(false);
      return;
    }
    setBriefs((data || []).map(b => {
      const recips = b.brief_recipients || [];
      return {
        ...b,
        owners_list: (b.brief_owners || []).map(bo => bo.internal_users).filter(Boolean),
        response_count: b.brief_responses?.[0]?.count || 0,
        // Submitted-quote count (status='responded') drives the at-risk rule;
        // recipient_count sizes the engagement summary. Both from recipient
        // status only — never from draft content (§7).
        recipient_count: recips.length,
        submitted_count: recips.filter(r => r.status === 'responded').length,
      };
    }));
    if (!silent) setLoading(false);
    const ids = (data || []).map(b => b.id);
    fetchTagsFor('brief', ids).then(setTagsByBrief);
    // Draft presence (existence only, §7) for the engagement mix / Responses
    // column in the at-risk view.
    fetchDraftPresenceForBriefs(ids).then(setDraftsByBrief);
    // Live comment counts — separate query (PostgREST can't filter inside
    // an embedded count). Slim payload: just brief_id per non-deleted row,
    // grouped client-side. Map<brief_id, count> consumed by BriefsBoard /
    // list row for the 💬 N indicator.
    supabase
      .from('brief_comments')
      .select('brief_id')
      .eq('deleted', false)
      .in('brief_id', ids)
      .then(({ data: rows }) => {
        const map = new Map();
        for (const r of rows || []) map.set(r.brief_id, (map.get(r.brief_id) || 0) + 1);
        setCommentCountsByBrief(map);
      });
  };

  // Manual archive from the briefs list. Choreography is layered:
  //   1. Click → window.confirm() (existing behaviour, no change).
  //   2. archivingIds gets the brief id → the <tr> picks up the slide+fade
  //      class (defined in archiveAnimation.css). Table rows can't smoothly
  //      collapse (max-height transitions don't apply to <tr>), so we rely
  //      on the post-write refetch to drop the row from the DOM once the
  //      DB row has flipped archived_at.
  //   3. DB write fires in parallel — briefs.archived_at + brief_archive_history
  //      audit row (mirrors BriefDetail.handleArchiveToggle for parity).
  //   4. After ~250ms the row is removed by the refetch; the Undo toast
  //      appears at ~500ms with a 6-second countdown bar.
  //   5. Clicking Undo runs unarchiveFromUndo() — reverses the DB write,
  //      writes an 'unarchive' history row, refetches.
  const archiveFromList = (brief) => {
    if (!isAdmin(currentInternalUser)) return;
    if (!brief || brief.archived_at) return;
    if (archivingIds.has(brief.id)) return;
    if (!window.confirm(`Archive "${brief.brief_name}"? It'll be hidden from the default Briefs list and from every supplier's portal. You can unarchive at any time.`)) return;
    setArchivingIds(prev => { const next = new Set(prev); next.add(brief.id); return next; });
    const startedAt = Date.now();
    (async () => {
      const archivedAt = new Date().toISOString();
      const actorId = currentInternalUser?.id || null;
      const { error: e } = await supabase.from('briefs').update({
        archived_at: archivedAt,
        archived_by: actorId,
      }).eq('id', brief.id);
      if (e) {
        // DB failed — back out the animation state so the row settles
        // back in place and surface the error in the toast tray.
        setArchivingIds(prev => { const next = new Set(prev); next.delete(brief.id); return next; });
        notify({ eyebrow: "Couldn't archive", title: e.message, tone: 'archive' });
        return;
      }
      await supabase.from('brief_archive_history').insert([{
        brief_id: brief.id,
        action: 'archive',
        actor_id: actorId,
      }]);
      // Refetch after a beat so the row removal lands as the toast appears
      // rather than yanking out from under the fade. Toast itself is
      // sync'd to the same 500ms mark so it feels like one coordinated
      // moment.
      setTimeout(() => {
        getBriefs();
        setArchivingIds(prev => { const next = new Set(prev); next.delete(brief.id); return next; });
      }, 250);
      notify(
        {
          eyebrow: 'Archived',
          title: brief.brief_name || 'Brief archived',
          body: 'Filed and out of the active list.',
          tone: 'archive',
          action: { label: 'Undo', onClick: () => unarchiveFromUndo(brief) },
        },
        { holdMs: 6000, syncWith: startedAt },
      );
    })();
  };

  const unarchiveFromUndo = async (brief) => {
    // Match BriefDetail.handleArchiveToggle's unarchive path so the row
    // comes back with a fresh auto_archive_scheduled_for. Effective weeks
    // = per-brief override || system setting (refetched in case admin
    // changed it during the Undo window). The override column is NOT in
    // the list-view SELECT (keeps the briefs query lean + sidesteps
    // any RLS quirks), so we fetch it on-demand here for the one row
    // being restored.
    const [{ data: weeksRow }, { data: overrideRow }] = await Promise.all([
      supabase.from('app_settings').select('value').eq('key', 'auto_archive_briefs_weeks').single(),
      supabase.from('briefs').select('auto_archive_weeks_override').eq('id', brief.id).single(),
    ]);
    const systemWeeks = Math.max(1, Math.min(52, parseInt(weeksRow?.value, 10) || 4));
    const override = Number(overrideRow?.auto_archive_weeks_override);
    const weeks = Number.isFinite(override) && override > 0 ? Math.max(1, Math.min(52, override)) : systemWeeks;
    const { error: e } = await supabase.from('briefs').update({
      archived_at: null,
      archived_by: null,
      auto_archive_scheduled_for: new Date(Date.now() + weeks * 7 * 86400000).toISOString(),
    }).eq('id', brief.id);
    if (e) {
      notify({ eyebrow: "Couldn't undo", title: e.message, tone: 'archive' });
      return;
    }
    // Audit trail records both halves of the misclick so the timeline
    // doesn't pretend the archive never happened (it did, just briefly).
    await supabase.from('brief_archive_history').insert([{
      brief_id: brief.id,
      action: 'unarchive',
      actor_id: currentInternalUser?.id || null,
    }]);
    await getBriefs();
    notify({
      eyebrow: 'Restored',
      title: brief.brief_name || 'Brief restored',
      body: 'Back on the active list.',
      tone: 'success',
    });
  };

  const categories = Array.from(
    new Set(briefs.map(b => (b.brief_category || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  const projectNumbers = Array.from(
    new Set(briefs.map(b => (b.fusion_project_number || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  const clients = Array.from(
    new Set(briefs.map(b => (b.client || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  const owners = Array.from(
    new Map(
      briefs.flatMap(b => b.owners_list || []).map(u => [u.id, u])
    ).values()
  ).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));

  const query = search.trim().toLowerCase();
  const now = new Date();
  // Privileged briefs are admin / super-user only — non-admin internal
  // users (Finance, etc.) shouldn't even see them in the list. Gate
  // applied at the filter pipeline so all derived counts, drills, and
  // banners agree about which briefs exist.
  const canSeePrivileged = isAdmin(currentInternalUser);
  // At-risk (§1) — the shared rule: live 'sent' brief, deadline inside the
  // window, too few submitted quotes (exclusive handled). Archived briefs are
  // parked, never at-risk. Defined here (above `filtered`) so the filter,
  // banner and counts all read one derivation.
  const briefAtRisk = (b) => !b.archived_at && isBriefAtRisk(b, { submittedCount: b.submitted_count || 0, now: now.getTime() });
  // Engagement mix for a brief — recipient status + draft PRESENCE only (§7).
  const EMPTY_SET = new Set();
  const engagementMixFor = (b) => engagementMix(
    (b.brief_recipients || []).map(r => ({ status: r.status, hasDraft: (draftsByBrief.get(b.id) || EMPTY_SET).has(r.supplier_id) })));
  const filtered = briefs.filter(b => {
    if (b.is_privileged && !canSeePrivileged) return false;
    if (atRiskOnly && !briefAtRisk(b)) return false;
    if (needsAttentionOnly) {
      if (b.status !== 'sent') return false;
      if (b.archived_at) return false;
      if (!b.required_response_time) return false;
      if (new Date(b.required_response_time) >= now) return false;
      if ((b.response_count || 0) > 0) return false;
    }
    // Archived hidden by default, but a search query surfaces them (with the
    // "Archived" badge) so archive never hides things from search.
    if (!showArchived && !query && b.archived_at) return false;
    if (statusFilter.length > 0 && !statusFilter.includes(b.status || 'draft')) return false;
    if (categoryFilter.length > 0 && !categoryFilter.includes((b.brief_category || '').trim())) return false;
    if (projectNumberFilter.length > 0 && !projectNumberFilter.includes((b.fusion_project_number || '').trim())) return false;
    if (clientFilter.length > 0 && !clientFilter.includes((b.client || '').trim())) return false;
    // Response sub-filter — set by Reporting drill-downs that want to slice
    // status='sent' into "received responses" vs "still awaiting".
    if (responseFilter === 'with' && (b.response_count || 0) === 0) return false;
    if (responseFilter === 'without' && (b.response_count || 0) > 0) return false;
    // Award-state sub-filter — splits status='closed' into Awarded vs
    // Closed-without-award, since the donut treats them as different buckets.
    if (awardStateFilter === 'awarded' && (b.status !== 'closed' || !b.awarded_supplier_id)) return false;
    if (awardStateFilter === 'closed_no_award' && (b.status !== 'closed' || b.awarded_supplier_id)) return false;
    // Awarded-to-supplier filter — straight column comparison since briefs
    // already carry awarded_supplier_id.
    if (awardedSupplierFilter && b.awarded_supplier_id !== awardedSupplierFilter) return false;
    // Responded-by-supplier filter — checked against the brief_id set
    // returned by the side fetch. While in-flight (respondedBriefIds is
    // null) every row is rejected so the table doesn't flash an
    // unfiltered state — the spinner is the empty-rows interim.
    if (respondingSupplierFilter) {
      if (respondedBriefIds === null) return false;
      if (!respondedBriefIds.has(b.id)) return false;
    }
    // Free-form date range — drives the Briefs-Sent windows (created_at) and
    // the Spend Awarded weekly bars (closed_at). field tells us which column
    // to compare against so the same filter shape works for both.
    if (dateRangeFilter) {
      const raw = dateRangeFilter.field === 'closed_at' ? b.closed_at : b.created_at;
      if (!raw) return false;
      const t = new Date(raw).getTime();
      if (isNaN(t)) return false;
      if (dateRangeFilter.start && t < new Date(dateRangeFilter.start).getTime()) return false;
      if (dateRangeFilter.end && t >= new Date(dateRangeFilter.end).getTime()) return false;
    }
    if (ownerFilter.length > 0) {
      const ownersList = b.owners_list || [];
      const ownerIds = ownersList.map(u => u.id);
      const matchesUnassigned = ownerFilter.includes('unassigned') && ownersList.length === 0;
      const matchesById = ownerFilter.some(v => v !== 'unassigned' && ownerIds.includes(v));
      if (!matchesUnassigned && !matchesById) return false;
    }
    // Tag filter — AND semantics: the brief must carry every selected tag.
    if (briefTagFilter.length > 0) {
      const ids = new Set((tagsByBrief.get(b.id) || []).map(t => t.id));
      if (!briefTagFilter.every(id => ids.has(id))) return false;
    }
    if (query) {
      const ownerNames = (b.owners_list || []).map(u => u.full_name).filter(Boolean);
      const hit = [b.brief_name, b.fusion_project_name, b.fusion_project_number, b.client, b.brief_category, ...ownerNames]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(query));
      if (!hit) return false;
    }
    return true;
  });
  const filtersActive = query || statusFilter.length > 0 || categoryFilter.length > 0 || ownerFilter.length > 0 || projectNumberFilter.length > 0 || clientFilter.length > 0 || briefTagFilter.length > 0 || needsAttentionOnly || atRiskOnly || responseFilter !== 'all' || awardStateFilter !== 'all' || dateRangeFilter || drillBanner || awardedSupplierFilter || respondingSupplierFilter;

  // One-call reset used by both the toolbar's Clear all and the drill
  // banner's Clear action — keeps the two affordances in lockstep.
  // fromReporting is intentionally NOT cleared here per spec: a user who
  // arrived from Reporting can clear the drill filters but still see the
  // Back-to-Reporting affordance so they can return without using the
  // browser back button.
  const clearAllFilters = () => {
    setSearch('');
    setStatusFilter([]);
    setCategoryFilter([]);
    setOwnerFilter([]);
    setProjectNumberFilter([]);
    setClientFilter([]);
    setBriefTagFilter([]);
    setNeedsAttentionOnly(false);
    setAtRiskOnly(false);
    setResponseFilter('all');
    setAwardStateFilter('all');
    setDateRangeFilter(null);
    setDrillBanner(null);
    setAwardedSupplierFilter(null);
    setRespondingSupplierFilter(null);
    setRespondedBriefIds(null);
    setSortField('required_response_time');
    setSortDirection('asc');
    // Wipe URL drill params too so a refresh after Clear lands clean.
    // ref=reporting is preserved when present so the Back-to-Reporting
    // affordance keeps working (spec: link survives Clear).
    const next = new URLSearchParams();
    if (fromReporting) next.set('ref', 'reporting');
    setSearchParams(next, { replace: true });
  };

  // Back-to-Reporting handler — Reporting moved out of /briefs into its
  // own /reporting route under System, so the user lands back on the
  // Briefs sub-tab there. Clear the local drill before navigating away
  // so a later return to /briefs lands clean.
  const handleBackToReporting = () => {
    clearAllFilters();
    setFromReporting(false);
    setSearchParams(new URLSearchParams(), { replace: true });
    navigate('/reporting?tab=briefs');
  };

  // Mount-once URL-param read. Translates the canonical drill keys
  // (?status, ?since, ?responses, etc — see src/lib/reportingDrill.js)
  // into local filter state. Runs exactly once via a ref guard so
  // subsequent setSearchParams calls from drill clicks don't re-fire
  // and re-seed mid-interaction.
  const initialDrillReadRef = useRef(false);
  useEffect(() => {
    if (initialDrillReadRef.current) return;
    initialDrillReadRef.current = true;
    const drill = parseBriefsDrillParams(searchParams);
    if (drill.status && drill.status.length > 0) setStatusFilter(drill.status);
    if (drill.category) setCategoryFilter([drill.category]);
    if (drill.owner) setOwnerFilter([drill.owner]);
    if (drill.responses === '0')   setResponseFilter('without');
    if (drill.responses === '1+')  setResponseFilter('with');
    if (drill.awardState === 'awarded')         setAwardStateFilter('awarded');
    if (drill.awardState === 'closed_no_award') setAwardStateFilter('closed_no_award');
    if (drill.awardedTo)   setAwardedSupplierFilter(drill.awardedTo);
    if (drill.respondedBy) setRespondingSupplierFilter(drill.respondedBy);
    if (drill.deadline === 'past' && drill.responses === '0') setNeedsAttentionOnly(true);
    if (drill.sort) {
      const sortMap = {
        created_desc:        { field: 'created_at',           direction: 'desc' },
        amount_desc:         { field: 'awarded_amount',       direction: 'desc' },
        time_to_award_desc:  { field: 'time_to_award',        direction: 'desc' },
        days_in_status_desc: { field: 'days_in_status',       direction: 'desc' },
      };
      const s = sortMap[drill.sort];
      if (s) { setSortField(s.field); setSortDirection(s.direction); }
    }
    if (drill.fromReporting) {
      setFromReporting(true);
      setActiveTab('list');
      // Banner precedence: explicit descriptor + tone in the URL takes
      // priority (rich copy survives refresh), then the named banner
      // key lookup, then a generic fallback.
      let banner;
      if (drill.bannerDescriptor) {
        banner = { tone: drill.bannerTone || 'teal', descriptor: drill.bannerDescriptor };
      } else if (drill.banner && BRIEFS_BANNER_DEFAULTS[drill.banner]) {
        banner = BRIEFS_BANNER_DEFAULTS[drill.banner];
      } else {
        banner = { tone: 'teal', descriptor: 'from Reporting' };
      }
      setDrillBanner(banner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort the filtered list by the active column. Missing values sink to
  // the bottom regardless of sort direction so blank cells don't pollute
  // the leading rows. Date fields compare numerically; everything else
  // uses localeCompare for a natural alphabetical order.
  const sortableFields = {
    brief_name: { type: 'string', getter: b => b.brief_name },
    fusion_project_name: { type: 'string', getter: b => b.fusion_project_name },
    required_response_time: { type: 'date', getter: b => b.required_response_time },
    // Drill-only sorts — set by Spend Awarded (amount desc) and Avg Time to
    // Award (time-to-award asc). No column-header UI for them today; users
    // see the order land via the row sort, and Clear all resets to default.
    awarded_amount: { type: 'number', getter: b => b.awarded_amount },
    time_to_award: { type: 'number', getter: b => {
      if (!b.closed_at || !b.created_at) return null;
      return new Date(b.closed_at).getTime() - new Date(b.created_at).getTime();
    } },
  };
  const sortedBriefs = (() => {
    // Pinned-first comparator — applied BEFORE the user's chosen sort
    // so pinned briefs always rise to the top of the flat list
    // (groupBy='none') and to the top of each group (groupBy='project'
    // / 'owner' / etc.). Mirrors the Suppliers list's pinned-first
    // default per the 2026-06 unified grouped-list brief: pinned is
    // the default ordering on both pages, not a toggle.
    const pinFirst = (a, b) => {
      const ap = userPinnedBriefIds.has(a.id) ? 0 : 1;
      const bp = userPinnedBriefIds.has(b.id) ? 0 : 1;
      return ap - bp;
    };
    const cfg = sortableFields[sortField];
    if (!cfg) {
      // No user sort active — just pinned-first then leave default order.
      return [...filtered].sort(pinFirst);
    }
    const dir = sortDirection === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const byPin = pinFirst(a, b);
      if (byPin !== 0) return byPin;
      const av = cfg.getter(a);
      const bv = cfg.getter(b);
      if (cfg.type === 'number') {
        const an = (av == null || isNaN(av)) ? null : Number(av);
        const bn = (bv == null || isNaN(bv)) ? null : Number(bv);
        if (an == null && bn == null) return 0;
        if (an == null) return 1;
        if (bn == null) return -1;
        return (an - bn) * dir;
      }
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      if (cfg.type === 'date') return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  })();

  // Urgency derived from status + deadline. Only sent briefs that
  // aren't archived can be overdue or due-this-week; closed/draft/
  // cancelled briefs always read as non-urgent because the team has
  // already moved on. Defined here (rather than next to briefPill below)
  // because statusEffectiveKey + overdueCountIn need it during the
  // `groups` IIFE that runs further up the component body — keeping it
  // below put it in the temporal dead zone for the Group-by-Status
  // path and rendered a white page.
  const briefUrgency = (brief) => {
    if (brief.status !== 'sent' || brief.archived_at) return null;
    if (!brief.required_response_time) return null;
    const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
    const sevenDaysOut = new Date(todayMid); sevenDaysOut.setDate(sevenDaysOut.getDate() + 7);
    const deadlineDate = new Date(brief.required_response_time);
    if (deadlineDate < todayMid) return 'overdue';
    if (deadlineDate <= sevenDaysOut) return 'due_this_week';
    return null;
  };

  // Effective status state — collapses raw status + derived urgency
  // into the buckets the team actually thinks about. Drives both the
  // status pill and the Group-by-Status bucketing.
  const statusEffectiveKey = (b) => {
    if (b.status === 'draft') return 'draft';
    if (b.status === 'cancelled') return 'cancelled';
    if (b.status === 'closed') return b.awarded_supplier_id ? 'awarded' : 'closed';
    if (b.status === 'sent') {
      const urg = briefUrgency(b);
      if (urg === 'overdue') return 'overdue';
      // Per build brief §2: "Due Soon" is a deadline-derived flag, not a
      // status bucket. Group-by-Status no longer renders a Due Soon group;
      // due-soon briefs sort into the regular Sent bucket and surface
      // their urgency via the deadline cell only.
      return 'sent';
    }
    return 'sent';
  };
  // Priority order for status grouping — most urgent first so the
  // user lands on the work that needs attention. Cancelled sinks to
  // the bottom because it's history.
  const STATUS_PRIORITY = ['overdue', 'sent', 'draft', 'awarded', 'closed', 'cancelled'];
  const STATUS_LABELS = {
    overdue: 'Overdue',
    sent: 'Sent',
    draft: 'Draft',
    awarded: 'Awarded',
    closed: 'Complete',
    cancelled: 'Cancelled',
  };
  // Group key for a brief based on the active groupBy. Empty values land
  // in a single "__none" bucket so they get a clear group header instead
  // of disappearing into a meaningless empty key.
  const groupKeyFor = (b) => {
    if (groupBy === 'project') return (b.fusion_project_number || '').trim() || '__none';
    if (groupBy === 'owner') return b.owner_id || '__none';
    if (groupBy === 'status') return statusEffectiveKey(b);
    // Pinned grouping — splits into "pinned" and "other" buckets so
    // the operator's saved set rises to the top of the table without
    // losing the rest of the list. Driven by the same userPinnedBriefIds
    // set the star button in the leftmost column toggles.
    if (groupBy === 'pinned') return userPinnedBriefIds.has(b.id) ? 'pinned' : 'other';
    return '__all';
  };
  // Group-meta helpers (earliestDeadlineIn, overdueCountIn,
  // earliestOverdueIn, statusBreakdownIn, initialsFor, ownersTextFor)
  // removed in 2026-06 — they fed the elaborate per-group header
  // (avatars, multi-pill status counts, overdue chips, owner-text
  // meta) that the row-sync brief replaced with the shared compact
  // treatment. Per-group earliest deadline now lives inside
  // briefGroupMeta below; everything else dropped to honour the
  // "every group header on both pages renders with the same bar,
  // caret, count and meta styling" ask.
  // Build the grouped buckets in display order. "No X" buckets sink to
  // the bottom so the populated groups lead the page.
  const groups = (() => {
    if (groupBy === 'none') return null;
    const map = new Map();
    for (const b of sortedBriefs) {
      const k = groupKeyFor(b);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(b);
    }
    // Status grouping: surface the full lifecycle so users see every
    // bucket the system has, even ones with zero briefs. Order them by
    // priority (most urgent first) so the most actionable work leads.
    if (groupBy === 'status') {
      for (const k of STATUS_PRIORITY) {
        if (!map.has(k)) map.set(k, []);
      }
      return Array.from(map.entries()).sort((a, b) => {
        const ai = STATUS_PRIORITY.indexOf(a[0]);
        const bi = STATUS_PRIORITY.indexOf(b[0]);
        return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
      });
    }
    // Pinned grouping: 'pinned' bucket first, 'other' second. Always
    // surface both buckets even when one is empty so the operator
    // sees the explicit "no pins yet" / "everything else" framing.
    if (groupBy === 'pinned') {
      if (!map.has('pinned')) map.set('pinned', []);
      if (!map.has('other')) map.set('other', []);
      const order = ['pinned', 'other'];
      return order.map(k => [k, map.get(k)]);
    }
    return Array.from(map.entries()).sort((a, b) => {
      const aNone = a[0] === '__none' ? 1 : 0;
      const bNone = b[0] === '__none' ? 1 : 0;
      if (aNone !== bNone) return aNone - bNone;
      return a[0].localeCompare(b[0]);
    });
  })();
  // Auto-collapse defaults — re-runs only when groupBy changes so manual
  // toggles within a single grouping are preserved.
  //   - status grouping: empty buckets collapse by default (we always
  //     show the full lifecycle, but empty stages shouldn't grab focus).
  //   - any grouping with > 5 populated buckets: collapse everything so
  //     a long list doesn't drop the user into a wall of rows.
  useEffect(() => {
    if (!groups) { setCollapsedGroups(new Set()); return; }
    const populated = groups.filter(([, briefs]) => briefs.length > 0);
    if (populated.length > 5) {
      setCollapsedGroups(new Set(groups.map(([k]) => k)));
      return;
    }
    if (groupBy === 'status') {
      const empties = groups.filter(([, briefs]) => briefs.length === 0).map(([k]) => k);
      setCollapsedGroups(new Set(empties));
      return;
    }
    setCollapsedGroups(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);
  const expandAllGroups = () => setCollapsedGroups(new Set());
  const collapseAllGroups = () => {
    if (!groups) return;
    setCollapsedGroups(new Set(groups.map(([k]) => k)));
  };
  // allCollapsed / anyCollapsed: computed inside GroupedListTable now,
  // since the Expand all / Collapse all chrome lives there.

  // Per-group helpers consumed by GroupedListTable below.
  const briefGroupLabel = (key, items) => {
    if (groupBy === 'project') {
      if (key === '__none') return 'No project reference';
      const first = items?.[0];
      const projectName = first?.fusion_project_name;
      return projectName ? `${key} · ${projectName}` : key;
    }
    if (groupBy === 'owner') {
      if (key === '__none') return 'Unassigned';
      const first = items?.[0];
      const owner = (first?.owners_list || []).find(o => o?.id === key);
      return owner?.full_name || 'Unknown owner';
    }
    if (groupBy === 'status') return STATUS_LABELS[key] || key || 'Unknown';
    if (groupBy === 'pinned') return key === 'pinned' ? 'Pinned' : 'All briefs';
    return key;
  };
  const briefGroupMeta = (key, items) => {
    if (groupBy === 'status') return null;
    if (!items || items.length === 0) return null;
    const deadlines = items
      .map(b => b.required_response_time)
      .filter(Boolean)
      .map(d => new Date(d).getTime())
      .filter(t => !isNaN(t));
    if (deadlines.length === 0) return null;
    const earliest = new Date(Math.min(...deadlines));
    return `EARLIEST DEADLINE ${fmtDate(earliest)}`;
  };
  const briefGroupAccent = (key) => {
    if (groupBy === 'status') {
      return ({
        overdue:   'border-l-[#E0290A]',
        sent:      'border-l-[#51534A]',
        draft:     'border-l-[#51534A]',
        awarded:   'border-l-[#004733]',
        closed:    'border-l-[#51534A]',
        cancelled: 'border-l-[#E0290A]',
      })[key] || 'border-l-[#92D400]';
    }
    if (groupBy === 'owner' && key === '__none') return 'border-l-[#E0290A]';
    return 'border-l-[#92D400]';
  };

  // Single status pill that conveys workflow lifecycle state. Status is
  // separate from urgency: per build brief §2, "Due Soon" lives on the
  // deadline cell only (it's a derived flag from the date, not a
  // workflow state). Per §4, the colour band is:
  //   Draft              — muted mid-grey (#51534A bg @ 20%, #DADCCD text)
  //   Cancelled          — red (warning colour)
  //   Closed             — flat grey
  //   Awarded            — green (RESERVED for this state only)
  //   Sent — Overdue     — red (urgency)
  //   Sent — Awaiting    — teal (info, supplier has engaged)
  //   Sent (no response) — neutral light grey (delivered, awaiting movement)
  // Green is RESERVED for the Awarded state — no other status uses it.
  const briefPill = (brief) => {
    if (brief.status === 'draft') {
      return { label: 'Draft', className: 'bg-[#51534A]/20 text-fusion-mid-grey' };
    }
    if (brief.status === 'cancelled') {
      return { label: 'Cancelled', className: 'bg-[#E0290A]/15 text-[#E0290A]' };
    }
    if (brief.status === 'closed') {
      return brief.awarded_supplier_id
        ? { label: '✓ Awarded', className: 'bg-[#009159]/[0.12] text-[#009159]' }
        : { label: 'Closed', className: 'bg-[#51534A]/15 text-[#51534A]' };
    }
    if (brief.status === 'sent') {
      const urg = briefUrgency(brief);
      if (urg === 'overdue') {
        return { label: 'Overdue', className: 'bg-[#E0290A]/15 text-[#E0290A]' };
      }
      if ((brief.response_count || 0) > 0) {
        return { label: 'Awaiting responses', className: 'bg-[#2E8FA1]/15 text-[#2E8FA1]' };
      }
      return { label: 'Sent', className: 'bg-fusion-grey/15 text-fusion-mid-grey' };
    }
    return { label: brief.status || 'Draft', className: 'bg-[#51534A]/15 text-[#51534A]' };
  };

  // Small column-header arrow — empty/light when this isn't the active
  // sort, filled when it is, pointing in the active direction. The
  // inactive tone matches the unified header label colour (#7d7f76)
  // so sort + filter glyphs share one resting register; active goes
  // green and the parent header label tints green to match.
  const SortIndicator = ({ field }) => {
    const isActive = sortField === field;
    return (
      <span className={`inline-block ml-1 text-[10px] leading-none ${isActive ? 'text-fusion-forest' : 'text-fusion-mid-grey'}`} aria-hidden="true">
        {isActive ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    );
  };

  // Otto List briefing bar (build #10). Ladder surfaces the single top
  // item: overdue responses → drafts unsent → all moving. Only the
  // overdue state uses red. ("Responses ready to award" needs a
  // recipient-completion signal that isn't loaded on this page; the
  // Award celebration itself still fires via OttoAward.)
  // Overdue count lifted to page scope so the Overdue tab (§5) and the
  // briefing bar read ONE number — same "deadline < start of today"
  // definition BriefsOverdueList renders off, so tab, banner and list
  // can't disagree. At-risk / draft counts derived once here too, and fed
  // both into the bar and into its dismissal signature.
  const overdueCount = briefs.filter(b => statusEffectiveKey(b) === 'overdue').length;
  const atRiskBriefsList = briefs.filter(briefAtRisk);
  const draftCount = briefs.filter(b => statusEffectiveKey(b) === 'draft').length;
  // Dismissal signature — every actionable count the bar can surface. Hidden
  // only while it matches what was dismissed; any change re-shows the bar.
  const briefsBarSig = `${atRiskBriefsList.length}|${questionsWaiting}|${extensionsWaiting}|${chasesWaiting}|${overdueCount}|${draftCount}`;
  const ottoBriefsBarHidden = ottoBarDismissedSig === briefsBarSig;
  const briefsLifecycle = (() => {
    // Merged band (§3.1): at-risk (still winnable, chase now) FIRST, then
    // overdue (history). Each half is a segment with its own link; a zero half
    // is omitted; both zero → fall through to drafts / all-clear. No second
    // banner is ever stacked — the owl already is the "worth a chase" affordance.
    const atRiskBriefs = atRiskBriefsList;
    const overdue = overdueCount;
    const drafts = draftCount;
    const viewAtRisk = () => { setActiveTab('list'); setNeedsAttentionOnly(false); setAtRiskOnly(true); setSortField('required_response_time'); setSortDirection('asc'); };
    const segments = [];
    if (atRiskBriefs.length > 0) {
      // Useful detail from recipient status only (§7): suppliers who accepted
      // but haven't submitted — the chase-worthy ones.
      const engagedNotIn = atRiskBriefs.reduce(
        (n, b) => n + (b.brief_recipients || []).filter(r => r.status === 'accepted').length, 0);
      segments.push({
        dot: 'red',
        title: `${atRiskBriefs.length} brief${atRiskBriefs.length === 1 ? ' is' : 's are'} at risk`,
        body: engagedNotIn > 0
          ? `${engagedNotIn} supplier${engagedNotIn === 1 ? ' has' : 's have'} accepted but not submitted.`
          : 'Still winnable — worth a chase before the deadline.',
        ctaLabel: 'View at risk →',
        onCta: viewAtRisk,
      });
    }
    // Ordered + coloured by severity (waiting-on-you §3): questions (red — a
    // supplier is blocked from quoting) → extension requests and update
    // requests (both amber, and deliberately the same: one supplier can't
    // finish in time, the other may be holding a crew, and neither outranks the
    // other) → overdue (grey — nobody's waiting on a reply). Each line names the
    // cost; the first three all open the "Waiting on you" tab, overdue opens
    // the Overdue tab.
    if (questionsWaiting > 0) {
      segments.push({
        dot: 'red',
        title: `${questionsWaiting} supplier question${questionsWaiting === 1 ? '' : 's'} waiting`,
        body: `A supplier is blocked from quoting until you answer${questionsWaiting === 1 ? '' : ' — the oldest first'}.`,
        ctaLabel: 'Answer →',
        onCta: () => setActiveTab('waiting'),
      });
    }
    if (extensionsWaiting > 0) {
      segments.push({
        dot: 'amber',
        title: `${extensionsWaiting} request${extensionsWaiting === 1 ? '' : 's'} for more time`,
        // The cost is specifically that it expires — unanswered, it decides
        // itself when the brief closes.
        body: 'Decide before the brief closes, or the request answers itself and they quote in a rush or drop out.',
        ctaLabel: 'Review →',
        onCta: () => setActiveTab('waiting'),
      });
    }
    if (chasesWaiting > 0) {
      segments.push({
        dot: 'amber',
        title: `${chasesWaiting} supplier update request${chasesWaiting === 1 ? '' : 's'}`,
        body: 'A supplier may be holding a crew while they wait — respond or send a holding update.',
        ctaLabel: 'Respond →',
        onCta: () => setActiveTab('waiting'),
      });
    }
    if (overdue > 0) {
      segments.push({
        dot: 'grey',
        title: `${overdue} overdue`,
        body: "Deadline passed with no decision recorded — nobody's waiting on a reply, but worth closing off.",
        ctaLabel: 'View overdue →',
        onCta: () => setActiveTab('overdue'),
      });
    }
    if (segments.length > 0) return { segments };
    if (drafts > 0) return { dot: 'teal', title: `${drafts} draft${drafts === 1 ? " hasn't" : "s haven't"} been sent yet`, body: "Finish them off and get them out when you're ready.", ctaLabel: 'View drafts →', onCta: () => setStatusFilter(['draft']) };
    return { dot: null, title: "Everything's moving", body: 'No briefs at risk and no drafts sitting unsent — nice.' };
  })();
  const dismissBriefsBar = () => {
    try { sessionStorage.setItem('otto_briefs_bar_dismissed_sig', briefsBarSig); } catch (e) { /* ignore */ }
    setOttoBarDismissedSig(briefsBarSig);
  };

  return (
    <div className="h-screen bg-canvas flex">
      {/* Archive toast — bottom-right, ~6s Undo window. Single instance
          per page; ActionToast handles the stack internally. */}
      <ArchiveToast />

      {/* Sidebar — sticky so it stays visible while the page scrolls. */}
      <Sidebar currentInternalUser={currentInternalUser} />

      {/* Inner page column. h-screen explicitly clamps the column to
          the viewport. NOTE: overflow-hidden here was tried but it
          creates a nested scrollport that confuses position:sticky
          on the table's column headers — they anchor to this wrapper
          instead of to the table-area scroll viewport below.
          h-screen alone (without overflow) keeps the column height
          bounded while leaving the scroll area below as the single
          scrollport for sticky to resolve against. */}
      <div className="flex-1 flex flex-col min-w-0 h-screen">
        {/* Page header — inline H1 with tagline and CTA, divider beneath.
            Matches the Dashboard pattern; the divider is inset (mx-8) so
            it doesn't run edge-to-edge across the page. */}
        <div className="pt-8">
          <div className="px-8 flex items-start justify-between gap-6 mb-6">
            <div className="min-w-0">
              <h1 className="fusion-h1 text-ink-primary">Briefs</h1>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">Manage every brief from draft to award — track responses, compare quotes, and close the loop.</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 pt-1">
              <HowItWorksButton to="/briefs/how-it-works" />
              {/* AddButton wires up the shared + → scale(1.24)
                  hover + brightness lift + scale(.97) press from
                  the 2026-06 motion system. Navigates synchronously
                  so it skips the pending/success phases — straight
                  click → route. */}
              <AddButton variant="primary" label="Create Brief" onClick={() => navigate('/briefs/new')} />
            </div>
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        {/* Otto lifecycle briefing bar — above the tabs so it sits at the
            top of the page (matching the Suppliers page) and is visible in
            both List and Board views. Single top state via the ladder; ×
            hides for the day. Hidden while loading / empty workspace. */}
        {!loading && briefs.length > 0 && !ottoBriefsBarHidden && (
          <div className="mx-8 mt-6">
            <OttoPageBar {...briefsLifecycle} onDismiss={dismissBriefsBar} />
          </div>
        )}

        {/* Tab bar — split into two kinds (brief-briefs-tab-row §2, mirroring
            the compliance queue split): VIEWS (ways of looking at all briefs)
            on the left, then a divider + a "NEEDS YOU" label + the QUEUES of
            outstanding work. Queues carry a count badge that always renders —
            zero shows dimmed (§3) so "none outstanding" is distinct from a
            missing badge. Each group is its own tablist for a11y (§8). */}
        {(() => {
          const tabBase = "font-['Metropolis'] text-sm font-semibold px-5 py-3 transition-colors inline-flex items-center gap-2";
          const tabCls = (key) => `${tabBase} ${activeTab === key ? 'text-fusion-forest border-b-2 border-[#92D400]' : 'text-[#51534A] hover:text-fusion-mid-grey'}`;
          const views = [
            { key: 'list', label: 'List View', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            ) },
            { key: 'board', label: 'Board', icon: (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="3" width="6" height="18" rx="1"/><rect x="11" y="3" width="6" height="12" rx="1"/><rect x="19" y="3" width="2" height="6" rx="1"/>
              </svg>
            ) },
          ];
          // Urgency order: waiting (someone's stuck) → overdue → feedback due.
          const queues = [
            { key: 'waiting', label: 'Waiting on you', count: waitingTotal, tone: 'red', noun: 'items' },
            { key: 'overdue', label: 'Overdue', count: overdueCount, tone: 'grey', noun: 'briefs' },
            { key: 'feedback', label: 'Feedback due', count: feedbackDueCount, tone: 'amber', noun: 'briefs' },
            // Admin-only, and absent entirely for everyone else — not disabled,
            // not zero. Last in the row because it is the least time-critical:
            // a debrief waiting on a read is not a supplier waiting on a reply.
            ...(isAdmin(currentInternalUser)
              ? [{ key: 'review', label: 'Awaiting review', count: awaitingReviewCount, tone: 'amber', noun: 'debriefs' }]
              : []),
          ];
          const badgeOn = { red: 'bg-[#E0290A]/15 text-[#E0290A]', grey: 'bg-[#51534A]/15 text-[#51534A]', amber: 'bg-[#E89A2E]/20 text-[#7A4E10]' };
          return (
            <div className="mx-8 border-b border-line flex items-stretch flex-wrap">
              <div role="tablist" aria-label="Brief views" className="flex">
                {views.map(t => (
                  <button key={t.key} type="button" role="tab" aria-selected={activeTab === t.key}
                    onClick={() => setActiveTab(t.key)} className={tabCls(t.key)}>
                    {t.icon}{t.label}
                  </button>
                ))}
              </div>
              {/* Decorative divider — hidden from assistive tech (§8). */}
              <span aria-hidden="true" className="self-center mx-2 h-6 w-px bg-line" />
              <span className="self-center font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#9BA08C] pr-1">Needs you</span>
              <div role="tablist" aria-label="Needs you" className="flex">
                {queues.map(t => (
                  <button key={t.key} type="button" role="tab" aria-selected={activeTab === t.key}
                    onClick={() => setActiveTab(t.key)}
                    aria-label={`${t.label}, ${t.count} ${t.noun}`}
                    className={tabCls(t.key)}>
                    {t.label}
                    <span className={`text-[10px] font-extrabold tabular-nums rounded-full px-1.5 py-px ${t.count > 0 ? badgeOn[t.tone] : 'bg-[#EEF1E6] text-[#B4BCA8]'}`}>
                      {t.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {activeTab === 'waiting' ? (
          <WaitingOnYou questions={waitingQuestions} extensionRequests={waitingExtensions} updateRequests={waitingUpdates} />
        ) : activeTab === 'overdue' ? (
          <BriefsOverdueList />
        ) : activeTab === 'feedback' ? (
          <FeedbackDueList items={feedbackDueList} onOpen={(briefId) => navigate(`/briefs/${briefId}`)} />
        ) : activeTab === 'review' ? (
          <DebriefReviewQueue
            rows={reviewQueueRows}
            currentUserId={currentInternalUser?.id}
            onOpen={(d) => navigate(`/debriefs/${d.id}`)}
            onApprove={async (d) => {
              const { error, historyError } = await applyDebriefTransition({ debrief: d, action: 'approve', actor: currentInternalUser });
              if (error) { notify({ eyebrow: "Couldn't approve", title: error.message, tone: 'danger' }); return; }
              // The row leaves the queue on the WRITE succeeding, not on the
              // emails — the debrief is published either way, and leaving it
              // sitting there would invite a second approval.
              setPendingDebriefs(list => list.filter(x => x.id !== d.id));
              if (historyError) notify({ eyebrow: 'Published, but not recorded', title: historyError.message, tone: 'danger' });
              else notify({ eyebrow: 'Published', title: d.project_name || 'Debrief published', body: 'The author and any action owners have been told.', tone: 'success' });
              notifyDebriefTransition({ debrief: d, action: 'approve', actor: currentInternalUser });
            }}
            onRequestChanges={async (d) => {
              // The note is the whole point of sending it back — "changes
              // requested" with no note leaves the author guessing, so this
              // asks for one rather than transitioning on an empty reason.
              const note = window.prompt(`What needs changing in ${d.project_name || 'this debrief'}?`);
              if (note == null) return;
              if (!note.trim()) { notify({ eyebrow: 'Say what needs changing', title: 'The author only sees your note.', tone: 'danger' }); return; }
              const { error, historyError } = await applyDebriefTransition({ debrief: d, action: 'request_changes', note: note.trim(), actor: currentInternalUser });
              if (error) { notify({ eyebrow: "Couldn't send it back", title: error.message, tone: 'danger' }); return; }
              setPendingDebriefs(list => list.filter(x => x.id !== d.id));
              if (historyError) notify({ eyebrow: 'Sent back, but not recorded', title: historyError.message, tone: 'danger' });
              else notify({ eyebrow: 'Sent back for changes', title: d.project_name || 'Debrief', body: 'The author has your note.', tone: 'success' });
              notifyDebriefTransition({ debrief: d, action: 'request_changes', note: note.trim(), actor: currentInternalUser });
            }} />
        ) : activeTab === 'board' ? (
          <BriefsBoard
            briefs={briefs}
            currentUserId={currentInternalUser?.id}
            commentCountsByBrief={commentCountsByBrief} />
        ) : (
        <>
        {(drillBanner || fromReporting) && (() => {
          // Tone palette — amber for urgency (Needs Attention), green for
          // positive-outcome drills (Awarded / Spend), grey for neutral
          // closed-without-award, teal as the default informational tone.
          const tones = {
            amber: { bg: 'bg-[#E89A2E]/10', border: 'border-[#E89A2E]/30', text: 'text-[#7A4E10]' },
            green: { bg: 'bg-[#92D400]/10', border: 'border-[#92D400]/30', text: 'text-fusion-forest' },
            grey:  { bg: 'bg-[#51534A]/15', border: 'border-[#DADCCD]/40', text: 'text-fusion-mid-grey' },
            teal:  { bg: 'bg-[#2E8FA1]/10', border: 'border-[#2E8FA1]/30', text: 'text-[#2E8FA1]' },
          };
          const t = tones[drillBanner?.tone] || tones.teal;
          return (
            <div className={`mx-8 mt-6 ${t.bg} border ${t.border} rounded-xl px-4 py-3 flex items-center justify-between gap-4`}>
              <p className={`font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase ${t.text}`}>
                {drillBanner
                  ? <>Showing {filtered.length} brief{filtered.length === 1 ? '' : 's'} {drillBanner.descriptor}</>
                  : <>Arrived from Reporting</>}
              </p>
              <div className="flex items-center gap-4 flex-shrink-0">
                {fromReporting && (
                  <button
                    type="button"
                    onClick={handleBackToReporting}
                    className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-forest hover:text-fusion-forest transition-colors">
                    ← Back to Reporting
                  </button>
                )}
                {drillBanner && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-fusion-mid-grey transition-colors">
                    Clear
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        <div className="mx-8 mt-6 border-b border-line pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search briefs by name, project, category or owner..."
              className="flex-1 min-w-[280px]" />
            <GroupBySelect
              value={groupBy}
              onChange={setGroupBy}
              options={[
                { value: 'none', label: 'None' },
                { value: 'project', label: 'Project' },
                { value: 'owner', label: 'Owner' },
                { value: 'status', label: 'Status' },
                { value: 'pinned', label: 'Pinned' },
              ]} />
            {/* Show Archived — inline checkbox label. py-1.5 matches
                the height of SearchInput and GroupBySelect in the same
                row so the control cluster sits on one shared baseline
                rather than the previous py-2.5 making this read as a
                taller adjacent band. */}
            <label className="inline-flex items-center gap-2 cursor-pointer whitespace-nowrap bg-white border border-line rounded-xl px-3 py-1.5">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="w-4 h-4 accent-active-green" />
              <span className="font-['Metropolis'] text-[11px] font-bold tracking-[2px] uppercase text-[#51534A]">Show Archived</span>
            </label>
            {/* At-risk filter chip (§3.2) — removable, in the existing row. No
                new permanent control is added; it appears only while active. */}
            {atRiskOnly && (
              <button
                type="button"
                onClick={() => setAtRiskOnly(false)}
                className="inline-flex items-center gap-1.5 bg-[#E0290A]/10 border border-[#E0290A]/40 rounded-full px-3 py-1.5 font-['Metropolis'] text-[11px] font-bold text-[#8A1B08] hover:bg-[#E0290A]/[0.16] transition-colors">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E0290A]" aria-hidden="true" />
                At risk
                <span aria-hidden="true" className="text-[14px] leading-none">×</span>
                <span className="sr-only">Remove at-risk filter</span>
              </button>
            )}
            {filtersActive && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-active-green transition-colors px-3 py-2">
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* TagFilterBar lifted OUT of the scroll area so it stays
            visible alongside the search bar while the table scrolls.
            Previously this lived inside the scroll container and went
            with the table content, which made the filter row disappear
            on scroll while the column headers tried to stick. With it
            here, only table rows scroll — the entire filter / header
            chrome stays anchored. */}
        <div className="mx-8 mt-3">
          <TagFilterBar
            vocabulary="brief"
            selectedIds={briefTagFilter}
            onChange={setBriefTagFilter}
            matchCount={filtered.length}
            noun="brief"
          />
        </div>

        {/* Table scroll area. min-h-0 here is what actually unlocks
            position: sticky on the column headers — flex items default
            to min-height: auto, which lets them grow past their flex-1
            share when their content overflows. With min-h-0 the area
            keeps its flex-1 height, the table overflows INTERNALLY,
            and the sticky thead anchors to this viewport instead of
            leaking to the window. */}
        <div className="flex-1 overflow-auto px-8 pb-8 min-h-0">
          {loading ? (
            <div className="py-20"><OttoLoading size="large" message="Loading briefs" /></div>
          ) : filtered.length === 0 ? (
            filtersActive ? (
              // Empty-filter — Confused Otto + a directive that names the
              // active filters (a genuine human moment, per build #10).
              <div className="text-center py-16">
                <div className="flex justify-center mb-5"><OttoConfused size={104} /></div>
                <p className="font-['Metropolis'] text-[15px] font-bold text-ink-primary">No briefs match those filters</p>
                <p className="font-['Metropolis'] text-[13px] text-ink-2 mt-1.5 max-w-[44ch] mx-auto leading-relaxed">
                  {(() => {
                    const bits = [];
                    if (statusFilter.length) bits.push(statusFilter.join('/'));
                    const desc = bits.length ? ` in ${bits.join(' and ')}` : '';
                    const q = query ? ` matching “${query}”` : '';
                    return `Nothing${desc}${q}. Try clearing a filter or widening your search.`;
                  })()}
                </p>
                <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="bg-fusion-green border border-fusion-green rounded-[10px] text-[#14210A] font-['Metropolis'] text-xs font-bold tracking-[0.14em] uppercase px-4 py-2.5 hover:brightness-95 transition">
                    Clear filters
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/briefs/new')}
                    className="bg-white border border-fusion-emerald rounded-[10px] text-fusion-emerald font-['Metropolis'] text-xs font-bold tracking-[0.14em] uppercase px-4 py-2.5 hover:bg-fusion-emerald/[0.06] transition">
                    Create brief
                  </button>
                  {fromReporting && (
                    <button
                      type="button"
                      onClick={handleBackToReporting}
                      className="bg-white border border-line rounded-[10px] text-fusion-mid-grey font-['Metropolis'] text-xs font-bold tracking-[0.14em] uppercase px-4 py-2.5 hover:border-[#DADCCD] transition-colors">
                      ← Back to Reporting
                    </button>
                  )}
                </div>
              </div>
            ) : (
              // Genuinely empty workspace (first-run) keeps its own line.
              <div className="text-center py-20">
                <p className="font-['Metropolis'] text-[#51534A] tracking-[3px] uppercase text-sm">No briefs yet — create your first one!</p>
              </div>
            )
          ) : (
            <>
            {/* Summary row now owned by GroupedListTable below — the
                outlier highlight (e.g. "· 3 unassigned" in red) is
                passed via summaryExtra, and groupCountLabel surfaces
                the active grouping noun ("projects" / "owners" /
                "statuses") in the "across N" count copy. */}
            {/* Bulk-action toolbar — appears when row checkboxes have
                non-empty selection. Mirrors the Suppliers toolbar
                shape (list-page consistency brief B2): "N selected"
                + Clear selection. Bulk verbs (e.g. bulk archive,
                bulk apply tag) can attach here as labelled buttons
                without restructuring the toolbar — leaving inert for
                now per the brief's "until a concrete bulk action
                lands" option. */}
            {selectedForBulk.size > 0 && (
              <div className="relative flex items-center gap-3 bg-[#92D400]/[0.06] border border-[#92D400]/30 rounded-xl px-4 py-2.5 mb-4">
                <span className="font-['Metropolis'] text-xs font-bold text-ink">{selectedForBulk.size} selected</span>
                <button
                  type="button"
                  onClick={() => setSelectedForBulk(new Set())}
                  className="ml-auto font-['Metropolis'] text-[11px] font-bold tracking-[0.14em] uppercase text-[#51534A] hover:text-fusion-mid-grey transition-colors">
                  Clear selection
                </button>
              </div>
            )}
            {/* border-separate + border-spacing-0 is REQUIRED for
                sticky on any table element under this browser. Tailwind's
                preflight sets tables to `border-collapse: collapse` by
                default, and sticky silently no-ops under collapse mode.
                Spacing is zero so cells visually touch the same as
                collapse; rows use only `border-t` (no `border-b`) so we
                don't get double borders between adjacent rows. */}
            {(() => {
              // Briefs literally consumes the shared GroupedListTable
              // (2026-06 structural refactor). This page owns: the
              // column thead JSX, the row renderer, the summary
              // outlier extras (e.g. "· 3 unassigned" in red), and
              // the group-by helpers. GroupedListTable owns: <table>,
              // sticky thead container, summary row, sticky group
              // headers, tbody. Visual is byte-identical — one
              // source of truth for the chrome.
              const briefsThead = (
                <>
                  {/* Pin toggle — narrow leftmost column, no label.
                      Click flips user_pinned_briefs for the current user. */}
                  {/* Width widened to w-16 since the row cell now
                      carries star + bulk-select checkbox side-by-side
                      (list-page consistency brief B2 — leading
                      star+checkbox cluster matches Suppliers). */}
                  <th className={`${QUEUE_TH_CELL} w-16`} aria-label="Pin and bulk select" />
                  {/* Column widths tightened (build #10 §5): the short
                      columns hold a fixed w-[Npx] so they stop stretching,
                      while Brief keeps a min-width only — so it's the
                      flexible ANCHOR that absorbs the table's slack and
                      the rest stay put. Auto-layout, no truncation: a row
                      with unusually long content still wraps rather than
                      clipping. */}
                  <th className={`${QUEUE_TH_CELL} w-[104px]`}>
                    <FilterDropdown
                      label="Project Number"
                      options={projectNumbers.map(p => ({ value: p, label: p }))}
                      selected={projectNumberFilter}
                      onChange={setProjectNumberFilter} />
                  </th>
                  <th className={`${QUEUE_TH_CELL} w-[120px]`}>
                    <FilterDropdown
                      label="Client"
                      options={clients.map(c => ({ value: c, label: c }))}
                      selected={clientFilter}
                      onChange={setClientFilter} />
                  </th>
                  {/* Project — sortable by fusion_project_name. Unified
                      header treatment: muted #7d7f76 when not the active
                      sort, full green when active (label + glyph). */}
                  <th className={`${QUEUE_TH_CELL} w-[184px]`}>
                    <button
                      type="button"
                      onClick={() => toggleSort('fusion_project_name')}
                      className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase whitespace-nowrap hover:text-fusion-mid-grey transition-colors inline-flex items-center gap-1 ${sortField === 'fusion_project_name' ? 'text-fusion-forest' : 'text-fusion-mid-grey'}`}>
                      Project
                      <SortIndicator field="fusion_project_name" />
                    </button>
                  </th>
                  <th className={`${QUEUE_TH_CELL} min-w-[260px]`}>
                    <button
                      type="button"
                      onClick={() => toggleSort('brief_name')}
                      className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase whitespace-nowrap hover:text-fusion-mid-grey transition-colors inline-flex items-center gap-1 ${sortField === 'brief_name' ? 'text-fusion-forest' : 'text-fusion-mid-grey'}`}>
                      Brief
                      <SortIndicator field="brief_name" />
                    </button>
                  </th>
                  <th className={`${QUEUE_TH_CELL} w-[188px]`}>
                    <FilterDropdown
                      label="Brief Owner"
                      options={[
                        { value: 'unassigned', label: 'Unassigned' },
                        ...owners.map(o => ({ value: o.id, label: o.full_name })),
                      ]}
                      selected={ownerFilter}
                      onChange={setOwnerFilter} />
                  </th>
                  <th className={`${QUEUE_TH_CELL} w-[124px]`}>
                    {/* §3.3 — becomes the Responses column in the at-risk view. */}
                    {atRiskOnly ? (
                      <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-fusion-mid-grey">Responses</span>
                    ) : (
                      <FilterDropdown
                        label="Category"
                        options={categories.map(c => ({ value: c, label: c }))}
                        selected={categoryFilter}
                        onChange={setCategoryFilter} />
                    )}
                  </th>
                  <th className={`${QUEUE_TH_CELL} w-[120px]`}>
                    <button
                      type="button"
                      onClick={() => toggleSort('required_response_time')}
                      className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase whitespace-nowrap hover:text-fusion-mid-grey transition-colors inline-flex items-center gap-1 ${sortField === 'required_response_time' ? 'text-fusion-forest' : 'text-fusion-mid-grey'}`}>
                      Deadline
                      <SortIndicator field="required_response_time" />
                    </button>
                  </th>
                  {groupBy !== 'status' && (
                    <th className={`${QUEUE_TH_CELL} w-[130px]`}>
                      <FilterDropdown
                        label="Status"
                        options={[
                          { value: 'draft', label: 'Draft' },
                          { value: 'sent', label: 'Sent' },
                          { value: 'closed', label: 'Complete' },
                          { value: 'cancelled', label: 'Cancelled' },
                        ]}
                        selected={statusFilter}
                        onChange={setStatusFilter} />
                    </th>
                  )}
                  {/* Row-actions column. No header label — the trigger is
                      visible on row hover only and admin-only. Pinned to
                      the right of the table so it doesn't compete with the
                      content columns. */}
                  <th className={`${QUEUE_TH_CELL} text-right w-10`} aria-label="Row actions" />
                </>
              );
              // Outlier highlight — surfaces the __none group count
              // (briefs without an assigned owner / project / status)
              // in red so admins see "3 unassigned" / "2 untyped" /
              // "1 with no project" at a glance without scrolling
              // to the bucket itself. Mirrors the pre-refactor copy.
              const noneEntry = groups ? groups.find(([k]) => k === '__none') : null;
              const noneCount = noneEntry ? noneEntry[1].length : 0;
              const noneNoun = { project: 'with no project', owner: 'unassigned', status: 'untyped' }[groupBy] || 'other';
              const summaryExtra = noneCount > 0 ? (
                <> · <span className="text-[#E0290A] font-semibold">{noneCount} {noneNoun}</span></>
              ) : null;
              const groupCountLabel = { project: 'projects', owner: 'owners', status: 'statuses' }[groupBy] || null;
                  const renderBriefRow = (brief, idx) => {
                    const urgency = briefUrgency(brief);
                    // Per build brief §7: row-level left-edge accent removed.
                    // Urgency is conveyed via (a) deadline-cell text + chip and
                    // (b) status pill; pinned state lives on the star column.
                    const deadlineTone = urgency === 'overdue' ? 'text-[#E0290A]'
                      : urgency === 'due_this_week' ? 'text-[#2E8FA1]'
                      : 'text-fusion-mid-grey';
                    const pill = briefPill(brief);
                    const isPinned = userPinnedBriefIds.has(brief.id);
                    const isArchiving = archivingIds.has(brief.id);
                    const canArchiveFromList = isAdmin(currentInternalUser) && !brief.archived_at;
                    return (
                      <tr
                        key={brief.id}
                        // One row treatment across all four Briefs tabs. List
                        // view was the odd one out: taller rows (py-4 against
                        // py-3), its own divider and hover tokens, and zebra
                        // striping the other three don't have — so switching
                        // tabs changed the row height and the banding.
                        //
                        // Zebra goes rather than spreading to the other three:
                        // with a hairline under every row it was doing the same
                        // job twice, and it's the one thing that made this
                        // table read as a different screen.
                        className={`bg-white cursor-pointer ${QUEUE_ROW} group archive-row ${isArchiving ? 'archive-row--archiving' : ''}`}
                        onClick={() => navigate(`/briefs/${brief.id}`)}
                      >
                        {/* Pin + bulk-select. Same leading cluster
                            shape as the Suppliers list (list-page
                            consistency brief B2): pin star first as the
                            leftmost element, then the per-row checkbox
                            for bulk selection. Both controls
                            stopPropagation so clicking neither
                            triggers row navigation. */}
                        <td className={QUEUE_TD} data-row-control onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <PinButton
                              isPinned={isPinned}
                              onToggle={() => toggleBriefPin(brief.id)}
                              label={brief.brief_name || 'brief'} />
                            <input
                              type="checkbox"
                              checked={selectedForBulk.has(brief.id)}
                              onChange={() => toggleBulk(brief.id)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Select ${brief.brief_name || 'brief'} for bulk actions`}
                              className="w-4 h-4 accent-active-green flex-shrink-0 cursor-pointer"
                            />
                          </div>
                        </td>
                        {/* Ref — always renders the project number when
                            present; opacity / colour vary with the group
                            context. */}
                        <td className={QUEUE_TD}>
                          {brief.fusion_project_number ? (
                            <p className="font-['Metropolis'] tabular-nums text-[11px] tracking-wide text-[#51534A]">{brief.fusion_project_number}</p>
                          ) : (
                            <p className="font-['Metropolis'] text-sm text-[#51534A]">—</p>
                          )}
                        </td>
                        <td className={QUEUE_TD}>
                          {brief.client ? (
                            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{brief.client}</p>
                          ) : (
                            <p className="font-['Metropolis'] text-sm text-[#51534A]">—</p>
                          )}
                        </td>
                        <td className={QUEUE_TD}>
                          {brief.fusion_project_name ? (
                            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{brief.fusion_project_name}</p>
                          ) : (
                            <p className="font-['Metropolis'] text-sm text-[#51534A]">—</p>
                          )}
                        </td>
                        {/* Brief — name + state badges. */}
                        <td className={QUEUE_TD}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-['Metropolis'] text-sm font-bold leading-tight ${brief.archived_at ? 'text-[#51534A]' : 'text-ink'}`}>{brief.brief_name}</p>
                            {brief.archived_at && <span className="bg-[#51534A]/30 text-[#51534A] font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-full px-1.5 py-0.5">Archived</span>}
                            {brief.is_privileged && (
                              <span
                                title="Privileged brief — visible to Admin + Super User only"
                                className="inline-flex items-center justify-center text-[#7A4E10]"
                                aria-label="Privileged brief">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                              </span>
                            )}
                            {(() => {
                              const cc = commentCountsByBrief.get(brief.id) || 0;
                              if (cc === 0) return null;
                              return (
                                <span
                                  title={`${cc} internal comment${cc === 1 ? '' : 's'}`}
                                  className="inline-flex items-center gap-1 text-[#51534A] font-['Metropolis'] text-[10px] font-bold">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                  </svg>
                                  {cc}
                                </span>
                              );
                            })()}
                          </div>
                          {(() => {
                            const tlist = tagsByBrief.get(brief.id) || [];
                            if (tlist.length === 0) return null;
                            const shown = tlist.slice(0, 4);
                            const extra = tlist.length - shown.length;
                            return (
                              <div className="flex flex-wrap items-center gap-1 mt-1.5">
                                {shown.map(t => <TagChip key={t.id} tag={t} />)}
                                {extra > 0 && <span className="font-['Metropolis'] text-[10px] font-bold text-[#51534A]">+{extra} more</span>}
                              </div>
                            );
                          })()}
                        </td>
                        <td className={QUEUE_TD}>
                          {(brief.owners_list && brief.owners_list.length > 0) ? (
                            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                              {brief.owners_list.length <= 2
                                ? brief.owners_list.map(u => u.full_name).join(', ')
                                : `${brief.owners_list.slice(0, 1).map(u => u.full_name).join(', ')} +${brief.owners_list.length - 1}`}
                            </p>
                          ) : (
                            <p className="font-['Metropolis'] text-sm text-[#51534A]">—</p>
                          )}
                        </td>
                        <td className={QUEUE_TD}>
                          {/* §3.3 — in the at-risk view this column becomes
                              Responses (engagement bar + summary); otherwise it's
                              the Category it always was. */}
                          {atRiskOnly && briefAtRisk(brief) ? (
                            <EngagementBar mix={engagementMixFor(brief)} />
                          ) : brief.brief_category ? (
                            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{brief.brief_category}</p>
                          ) : (
                            <p className="font-['Metropolis'] text-sm text-[#51534A]">—</p>
                          )}
                        </td>
                        <td className={QUEUE_TD}>
                          {brief.required_response_time ? (
                            <>
                              <p className={`font-['Metropolis'] tabular-nums text-sm font-semibold ${deadlineTone}`}>
                                {fmtDate(brief.required_response_time)}
                              </p>
                              {/* Overdue chip removed (build #10) — the red
                                  deadline text above + the single Overdue
                                  status pill already carry it; a third red
                                  marker per row was redundant. Due Soon is a
                                  distinct (teal) signal and stays. */}
                              {urgency === 'due_this_week' && !briefAtRisk(brief) && (
                                <span className="inline-block mt-1 font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase rounded-full px-1.5 py-0.5 bg-[#2E8FA1]/15 text-[#2E8FA1] border border-[#2E8FA1]/30">
                                  Due Soon
                                </span>
                              )}
                              {/* At-risk flag (§3.3) — reads as text, not just a
                                  coloured edge (§8). Hours under 48h, else days. */}
                              {briefAtRisk(brief) && (() => {
                                const msLeft = new Date(brief.required_response_time).getTime() - now.getTime();
                                const short = msLeft < 48 * 3600000 ? `${Math.max(1, Math.floor(msLeft / 3600000))}h` : `${Math.round(msLeft / 86400000)}d`;
                                return (
                                  <span className="inline-block mt-1 font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase rounded-full px-1.5 py-0.5 bg-[#E0290A]/15 text-[#8A1B08] border border-[#E0290A]/35">
                                    At risk · {short}
                                  </span>
                                );
                              })()}
                            </>
                          ) : (
                            <p className="font-['Metropolis'] text-sm text-[#51534A]">—</p>
                          )}
                        </td>
                        {groupBy !== 'status' && (
                          <td className={QUEUE_TD}>
                            <span className={`inline-block font-['Metropolis'] text-[10px] font-bold tracking-[1px] uppercase rounded-full px-2 py-0.5 whitespace-nowrap ${pill.className}`}>
                              {pill.label}
                            </span>
                          </td>
                        )}
                        {/* Row actions — admin-only Archive trigger. Hidden
                            by default; opacity-0 → opacity-100 on row hover
                            via the `group` class on <tr>. Always rendered
                            (even when canArchiveFromList=false) so the cell
                            preserves table-column alignment. */}
                        <td className={`${QUEUE_TD} text-right whitespace-nowrap`}>
                          {canArchiveFromList && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); archiveFromList(brief); }}
                              disabled={isArchiving}
                              title="Archive brief"
                              aria-label={`Archive ${brief.brief_name || 'brief'}`}
                              className={`archive-button inline-flex items-center justify-center w-8 h-8 rounded-full border text-[#7A4E10] border-[#E89A2E]/40 hover:bg-[#E89A2E]/10 ${isArchiving ? 'archive-button--morphed' : ''} ${isArchiving ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'} transition-opacity disabled:cursor-not-allowed`}
                            >
                              <ArchiveIcon morphed={isArchiving} size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  };
              return (
                <GroupedListTable
                  thead={briefsThead}
                  colSpan={groupBy === 'status' ? 9 : 10}
                  groups={groups}
                  rows={sortedBriefs}
                  renderRow={renderBriefRow}
                  getRowKey={(b) => b.id}
                  getGroupLabel={briefGroupLabel}
                  getGroupMeta={briefGroupMeta}
                  getGroupAccent={briefGroupAccent}
                  noun="brief"
                  collapsedGroups={collapsedGroups}
                  onToggleGroup={toggleGroupCollapsed}
                  onCollapseAll={collapseAllGroups}
                  onExpandAll={expandAllGroups}
                  filteredCount={filtered.length}
                  summaryExtra={summaryExtra}
                  groupCountLabel={groupCountLabel}
                  isPinnedGroup={(key) => groupBy === 'pinned' && key === 'pinned'}
                />
              );
            })()}
            </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
